import {
  Heading,
  Popover,
  Portal,
  useDisclosure,
  Separator,
} from '@chakra-ui/react';
import { IQuoteRespSchema } from '@monetize/types/app';
import { ExperienceConfigurationKeys } from '@monetize/types/experience-config';
import { useQueryClient } from '@tanstack/react-query';
import { FC, useRef } from 'react';
import { useNavigate } from 'react-router';
import { handleApiErrorToast } from '../../../../../../api/axios';
import { useDealRoomConfig } from '../../../../../../api/settingsService';
import { useGetRecordShareEngagements } from '../../../../../../api/shareService';
import { MButton, MText } from '../../../../../../components/Monetize';
import { QuoteShareFormAndActivity } from '../../../../../../components/Quotes/QuoteCollaboration/QuoteShareFormAndActivity';
import { useQuoteShare } from '../../../../../../components/Quotes/QuoteCollaboration/useQuoteShare';
import { getDealRoomByDealRoom } from '../../../../../../constants/routes';
import { useCreateDealRoom } from '../../../../../../dealroom/api/dealRoomService';
import { useExperienceConfiguration } from '../../../../../../hooks';
import { useFlags } from '../../../../../../services/launchDarkly';
import { updateQuoteQueryCache } from '../../../../Quote/quoteUtils';

interface QuoteShareSendPopoverProps {
  isActionLoading: boolean;
  isDisabled?: boolean;
  quote: IQuoteRespSchema;
  setQuote: (quote: IQuoteRespSchema | null) => void;
  /**
   * Send via eSign or manual mark as sent button
   * Passed in as prop to avoid code duplication
   */
  children: (data: { isCreateDealRoomLoading }) => React.ReactNode;
}

interface SendQuoteActionProps {
  canCreateShareLink: boolean;
  hasRecordShare: boolean;
  isCreateDealRoomLoading: boolean;
  isShareEnabled: boolean;
  children: (data: { isCreateDealRoomLoading }) => React.ReactNode;
}

/**
 * The manual/eSign send action, or an explanation when the rep has no way to
 * advance the quote at all. Kept in its own component so the consumer's render
 * prop is only invoked while the popover is open (the Popover is `isLazy`).
 */
const SendQuoteAction = ({
  canCreateShareLink,
  hasRecordShare,
  isCreateDealRoomLoading,
  isShareEnabled,
  children,
}: SendQuoteActionProps) => {
  // Omitted once a record share exists, and for eSign quotes in tenants that
  // require Quote Share for eSign.
  const sendAction = hasRecordShare
    ? null
    : children({ isCreateDealRoomLoading });

  if (sendAction) {
    return (
      <>
        {isShareEnabled && <Separator my={6} />}
        {sendAction}
      </>
    );
  }

  // No send action and no share form leaves the popover with nothing to do.
  if (!hasRecordShare && !canCreateShareLink) {
    return (
      <MText mt={2} fontStyle="italic">
        You do not have permission to share or send this quote.
      </MText>
    );
  }

  return null;
};

export const QuoteShareSendPopover: FC<QuoteShareSendPopoverProps> = ({
  isActionLoading,
  isDisabled,
  quote,
  setQuote,
  children,
}: QuoteShareSendPopoverProps) => {
  const navigate = useNavigate();
  const { useDealRoom } = useFlags();
  const { quoteShareConfig } = useExperienceConfiguration(
    ExperienceConfigurationKeys.quoteAndContractSettings,
  );
  const { data: dealRoomConfig } = useDealRoomConfig();
  const { open, onClose, onOpen } = useDisclosure();
  const firstElementRef = useRef(null);

  const queryClient = useQueryClient();

  const { mutateAsync: doCreateDealRoom, isPending: isCreateDealRoomLoading } =
    useCreateDealRoom();

  const { data: engagements } = useGetRecordShareEngagements(quote.id, {
    enabled: quoteShareConfig?.enabled,
  });

  const quoteShareData = useQuoteShare({
    accessLevel: quote.accessLevel,
    engagements,
    quoteExpirationDate: quote.expirationDate,
    quoteId: quote.id,
    quoteStatus: quote.status,
  });

  const { canCreateShareLink, hasRecordShareRecords: hasRecordShare } =
    quoteShareData.externalShareState;
  const { isPending: isCreateShareLoading } =
    quoteShareData.dataQuery.addShareQuery;
  const { isPending: isRevokeShareLoading } =
    quoteShareData.dataQuery.revokeShareQuery;

  const handleCreateDealRoom = async () => {
    try {
      const dealRoom = await doCreateDealRoom({
        opportunityId: quote?.opportunity?.id!,
      });
      const updatedQuote = {
        ...quote,
        opportunity: {
          ...quote.opportunity,
          dealRoomId: dealRoom.id,
        },
      } as IQuoteRespSchema;
      updateQuoteQueryCache(queryClient, updatedQuote);
      setQuote(updatedQuote);
      navigate(getDealRoomByDealRoom(dealRoom.id));
    } catch (err) {
      handleApiErrorToast(err);
    }
  };

  return (
    <Popover.Root
      initialFocusEl={() => firstElementRef.current}
      open={open}
      closeOnInteractOutside={!isCreateShareLoading && !isRevokeShareLoading}
      size="sm"
      variant="popover"
      lazyMount
      unmountOnExit
      onOpenChange={(e) => {
        if (e.open) {
          onOpen();
        } else {
          onClose();
        }
      }}
      positioning={{
        placement: 'bottom-end',
      }}
    >
      <Popover.Trigger asChild>
        <MButton
          mr={1.5}
          variant="primary"
          data-testid="quote-send-btn"
          isLoading={isActionLoading}
          disabled={isDisabled || isActionLoading}
        >
          Sending Options
        </MButton>
      </Popover.Trigger>
      <Portal>
        <Popover.Positioner>
          <Popover.Content minW="400px">
            <Popover.Arrow />
            <Popover.Body>
              {/* Once quote has a record share created, then this is no longer available. User can revoke link to get this state back. */}
              {useDealRoom && dealRoomConfig?.enabled && !hasRecordShare && (
                <>
                  <Heading as="h4" fontSize="md" mb={2}>
                    Share Using Deal Room
                  </Heading>
                  <MText>
                    A Deal Room lets you showcase multiple quotes and share
                    marketing materials with your customer in one place.
                  </MText>
                  <MButton
                    variant="primary"
                    width="100%"
                    mt={2}
                    onClick={handleCreateDealRoom}
                    isDisabled={isCreateDealRoomLoading}
                    isLoading={isCreateDealRoomLoading}
                  >
                    Create Deal Room
                  </MButton>
                  <Separator my={6} />
                </>
              )}
              {quoteShareConfig?.enabled && (
                <>
                  <Heading as="h4" fontSize="md" mb={2}>
                    Share via Link
                  </Heading>

                  <MText>
                    Use this option to share a link that lets your customer
                    review the quote, update billing and other details, accept
                    the quote, and complete eSign (if configured).
                  </MText>

                  {hasRecordShare && (
                    <MText mt={2} fontStyle="italic">
                      If you want to manually progress the quote, revoke all
                      share links.
                    </MText>
                  )}

                  <QuoteShareFormAndActivity
                    quoteId={quote.id}
                    quoteShareData={quoteShareData}
                    engagements={engagements}
                    isDisabled={isCreateDealRoomLoading}
                  />
                </>
              )}

              <SendQuoteAction
                canCreateShareLink={canCreateShareLink}
                hasRecordShare={hasRecordShare}
                isCreateDealRoomLoading={isCreateDealRoomLoading}
                isShareEnabled={!!quoteShareConfig?.enabled}
              >
                {children}
              </SendQuoteAction>
            </Popover.Body>
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  );
};
